import { defineComponent, onMounted, PropType, ref, toRaw, watch } from "vue";
import {
    ArcElement,
    Chart,
    DoughnutController,
    Legend,
    Tooltip,
} from "chart.js";

Chart.register(ArcElement, DoughnutController, Legend, Tooltip);

export type DashboardDonutEntry = {
    label: string;
    value: number;
    color: string;
};

const NumberFormatter = new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 1,
    notation: "compact",
});

export default defineComponent({
    name: "DashboardDonut",

    props: {
        entries: {
            type: Array as PropType<DashboardDonutEntry[]>,
            required: true,
        },
    },

    setup(props) {
        const canvas = ref<HTMLCanvasElement>();
        const chart = ref<Chart<"doughnut">>();

        function updateChart() {
            if (!chart.value)
                return;

            const entries = toRaw(props.entries);

            chart.value.data.labels = entries.map((entry) => entry.label);
            chart.value.data.datasets[0]!.data = entries.map((entry) => entry.value);
            chart.value.data.datasets[0]!.backgroundColor = entries.map((entry) => entry.color);
            chart.value.update("active");
        }

        onMounted(() => {
            const entries = toRaw(props.entries);

            chart.value = new Chart(canvas.value!, {
                type: "doughnut",
                data: {
                    labels: entries.map((entry) => entry.label),
                    datasets: [{
                        data            : entries.map((entry) => entry.value),
                        backgroundColor : entries.map((entry) => entry.color),
                        borderColor     : "#101014",
                        borderWidth     : 3,
                        hoverBorderColor: "rgba(255, 255, 255, .72)",
                        hoverOffset     : 5,
                        spacing         : 1,
                    }],
                },
                options: {
                    responsive          : true,
                    maintainAspectRatio : false,
                    cutout              : "72%",
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            backgroundColor : "rgba(9, 9, 13, .97)",
                            borderColor     : "rgba(255, 255, 255, .12)",
                            borderWidth     : 1,
                            bodyColor       : "#f5f5f7",
                            displayColors   : true,
                            padding         : 12,
                            titleColor      : "#a8a8b2",
                            usePointStyle   : true,
                            callbacks: {
                                label(context) {
                                    const total = context.dataset.data.reduce((sum, value) => sum + Number(value), 0);
                                    const value = Number(context.raw);
                                    const percent = total ? value / total * 100 : 0;

                                    return ` ${NumberFormatter.format(value)} · ${percent.toFixed(1)} %`;
                                },
                            },
                        },
                    },
                },
            });
        });

        watch(() => props.entries, updateChart, { deep: true });

        return () => {
            const total = props.entries.reduce((sum, entry) => sum + entry.value, 0);

            return (
                <div class="dashboard-donut">
                    <div class="dashboard-donut-chart">
                        <canvas ref={canvas} />
                        <div class="dashboard-donut-total">
                            <span>Total</span>
                            <strong>{NumberFormatter.format(total)}</strong>
                        </div>
                    </div>

                    <div class="dashboard-donut-legend">
                        {props.entries.map((entry) => {
                            const percent = total ? entry.value / total * 100 : 0;

                            return (
                                <div key={entry.label}>
                                    <i style={{ background: entry.color }} />
                                    <span>{entry.label}</span>
                                    <strong>{NumberFormatter.format(entry.value)}</strong>
                                    <small>{percent.toFixed(1)} %</small>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        };
    },
});
